<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import AppGrid from '@/pages/core/grid/AppGrid.vue';
    import FullTime from '@/pages/core/icon/period/FullTime.vue';
    import HalfTime from '@/pages/core/icon/period/HalfTime.vue';
    import { useEventStore } from '@/stores/event';
    import { useUserStore } from '@/stores/user';
    import { EventFeedType, EventPeriod } from '@/constants';
    import { GameGridItem } from '@/models/gameGrid';
    import { useNumberFormatter } from '@/composables/useNumberFormatter';

    const {
        positionFTRegular = null,
        position1HRegular = null,
        positionFTCorners = null,
        position1HCorners = null,
    } = defineProps<{
        positionFTRegular?: string | null;
        position1HRegular?: string | null;
        positionFTCorners?: string | null;
        position1HCorners?: string | null;
    }>();

    onMounted(() => {
        console.log('Component %s mounted.', 'app-eventPositionSidePanel');
    });

    const eventStore = useEventStore();
    const userStore = useUserStore();
    const { formatNumber } = useNumberFormatter();

    let gameGridItem: GameGridItem;
    const isShowGameGrid = ref<boolean>(false);
    // const gameGridRef = ref(null);

    const { t } = useI18n();

    const positionFTRegularColor = computed(() => (Number(positionFTRegular) > 0 ? '#6aa84f' : '#f6475d'));
    const position1HRegularColor = computed(() => (Number(position1HRegular) > 0 ? '#6aa84f' : '#f6475d'));
    const positionFTCornersColor = computed(() => (Number(positionFTCorners) > 0 ? '#6aa84f' : '#f6475d'));
    const position1HCornersColor = computed(() => (Number(position1HCorners) > 0 ? '#6aa84f' : '#f6475d'));

    function showGameGrid(periodTypeId: number) {
        gameGridItem = new GameGridItem({
            periodTypeId: periodTypeId,
            customEventId: eventStore.customEventId ?? '',
            iso3: eventStore.iso3 ?? null,
            leagueName: eventStore.leagueName ?? '',
            teamHomeName: eventStore.teamHomeName ?? '',
            teamAwayName: eventStore.teamAwayName ?? '',
            teamHomeLogo: eventStore.teamHomeLogo ?? '',
            teamAwayLogo: eventStore.teamAwayLogo ?? '',
            cornersHome: eventStore.eventFeedType === EventFeedType.Live ? (eventStore.stats?.cornersHome ?? null) : 0,
            cornersAway: eventStore.eventFeedType === EventFeedType.Live ? (eventStore.stats?.cornersAway ?? null) : 0,
            scoreHome: eventStore.eventFeedType === EventFeedType.Live ? (eventStore.stats?.scoreHome ?? null) : 0,
            scoreAway: eventStore.eventFeedType === EventFeedType.Live ? (eventStore.stats?.scoreAway ?? null) : 0,
            jollywhere: eventStore.jollywhere,
            time: eventStore.stats?.time ?? null,
            eventFeedType: eventStore.eventFeedType ?? null,
        });

        // if (feedLine.mId !== DballMarket.Live) {
        //     feedLine.sc = '0-0';
        //     feedLine.cHome = 0;
        //     feedLine.cAway = 0;
        // } else {
        //     feedLine.sc = feedLine.score;
        //     feedLine.cHome = feedLine.corners_home;
        //     feedLine.cAway = feedLine.corners_away;
        // }

        // if (gameGridRef.value) {
        //     // Set data on AppGrid
        //     gameGridRef.value.periodTypeId = periodTypeId;
        //     gameGridRef.value.feedLine = feedLine;
        //     gameGridRef.value.balance = balance;
        //     // Open dialog and load data
        //     gameGridRef.value.$refs['gameGridDialogRef'].open();
        //     gameGridRef.value.loadData();
        // }

        isShowGameGrid.value = true;
    }
</script>

<template>
    <v-card elevation="0" style="overflow: auto; border-radius: 8px">
        <v-card-title class="font14 pt-1 pl-4 greyText font-weight-regular">
            <v-icon size="24" :icon="mdiCashRefund" />
            {{ t('eventStats.gamePositions') }}
        </v-card-title>
        <v-card-text>
            <v-row>
                <v-col cols="6" v-if="positionFTRegular" class="text-center py-2">
                    <v-chip
                        density="default"
                        variant="outlined"
                        :color="Number(positionFTRegular) > 0 ? 'tbGreen' : 'red'"
                        style="padding: 0 12px 0 0"
                        @click.stop="showGameGrid(EventPeriod.FullTime)"
                        class="font14"
                    >
                        <v-btn icon size="32" variant="text" class="no-hover" :ripple="false">
                            <FullTime :color="positionFTRegularColor" :size="32"></FullTime>
                        </v-btn>
                        {{ userStore.currency.symbol }}{{ formatNumber(positionFTRegular, 'money') }}
                        <v-icon :color="positionFTRegularColor" :size="16" class="ml-2" icon="$soccerBall" />
                    </v-chip>
                </v-col>
                <v-col cols="6" v-if="position1HRegular" class="text-center py-2">
                    <v-chip
                        density="default"
                        variant="outlined"
                        :color="Number(position1HRegular) > 0 ? 'tbGreen' : 'red'"
                        style="padding: 0 12px 0 0"
                        @click.stop="showGameGrid(EventPeriod.FirstHalf)"
                        class="font14"
                    >
                        <v-btn icon size="32" variant="text" class="no-hover" :ripple="false">
                            <HalfTime :color="position1HRegularColor" :size="24"></HalfTime>
                        </v-btn>
                        {{ userStore.currency.symbol }}{{ formatNumber(position1HRegular, 'money') }}

                        <v-icon :color="position1HRegularColor" :size="16" class="ml-2" icon="$soccerBall" />
                    </v-chip>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="6" v-if="positionFTCorners" class="text-center py-2">
                    <v-chip
                        density="default"
                        variant="outlined"
                        :color="Number(positionFTCorners) > 0 ? 'tbGreen' : 'red'"
                        style="padding: 0 12px 0 0"
                        @click.stop="showGameGrid(EventPeriod.FullTime)"
                        class="font14"
                    >
                        <v-btn icon size="32" variant="text" class="no-hover" :ripple="false">
                            <FullTime :color="positionFTCornersColor" :size="32"></FullTime>
                        </v-btn>
                        {{ userStore.currency.symbol }}{{ formatNumber(positionFTCorners, 'money') }}
                        <v-icon :color="positionFTCornersColor" :size="16" class="ml-2" icon="$cornerFlag" />
                    </v-chip>
                </v-col>
                <v-col cols="6" v-if="position1HCorners" class="text-center py-2">
                    <v-chip
                        density="default"
                        variant="outlined"
                        :color="Number(position1HCorners) > 0 ? 'tbGreen' : 'red'"
                        style="padding: 0 12px 0 0"
                        @click.stop="showGameGrid(EventPeriod.FirstHalf)"
                        class="font14"
                    >
                        <v-btn icon size="32" variant="text" class="no-hover" :ripple="false">
                            <HalfTime :color="position1HCornersColor" :size="24"></HalfTime>
                        </v-btn>
                        {{ userStore.currency.symbol }}{{ formatNumber(position1HCorners, 'money') }}
                        <v-icon :color="position1HCornersColor" :size="16" class="ml-2" icon="$cornerFlag" />
                    </v-chip>
                </v-col>
            </v-row>
        </v-card-text>
        <!--        <AppGrid ref="gameGridRef" :feedLine="event.feedLine" />-->
        <AppGrid v-model="isShowGameGrid" :gameGridItem="gameGridItem"></AppGrid>
    </v-card>
</template>

<style scoped></style>
